import type { ActionArgs, LoaderArgs, MetaFunction} from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import clsx from "clsx";
import { Form, Link, useActionData, useLoaderData, useTransition } from "@remix-run/react";
import dayjs from "dayjs";
import { createOGP } from "@/utilities/ogp";
import common from "@/data/common.json";
import { PageContainer } from "@/components/PageContainer";
import { ArticleLayout } from "@/components/ArticleLayout";
import { DisplayText } from "@/components/DisplayText";
import { Loading } from "@/components/Loader";
import { oneStopServices, reservationSchema } from "@/lib/schema";
import { sanitizeText } from "@/lib/format";
import { sendReservation } from "@/lib/email.server";
import { FieldsError } from "@/components/Fields/Error";
import { errorTextID, useID } from "@/components/utils";
import { FieldsInput } from "@/components/Fields/Input";
import { FieldsTextarea } from "@/components/Fields/Textarea";
import { createCSRFToken, verifyCSRFToken } from "@/lib/csrf.server";
import { commitSession, getSession } from "@/lib/sessions.server";
import { FieldsDateRangePicker } from "@/components/Fields/DateRangePicker";
import { FieldsRadioGroup } from "@/components/Fields/RadioGroup";
import { FieldsCheckboxGroup } from "@/components/Fields/CheckboxGroup";

export const meta: MetaFunction = () => {
  return createOGP({ path: `${common.meta.url}/contact/reservation`, pageName: "専用利用の仮申込" });
};

export async function loader({ request }: LoaderArgs) {
  const session = await getSession(request);
  const csrf = createCSRFToken();
  session.set("_csrf", csrf);
  const headers = await commitSession(session);

  return json({ csrf }, { headers });
}

export async function action({ request }: ActionArgs) {
  const session = await getSession(request);
  const formData = await request.formData();

  const verified = verifyCSRFToken(session, formData);
  if (!verified) {
    return json(
      {
        ok: false,
        errors: {
          formErrors: ["無効なリクエストが送信されました。"],
          fieldErrors: null,
        },
      },
      { status: 400 }
    );
  }

  const result = await reservationSchema.safeParseAsync({
    groupName: formData.get(`reservation[group_name]`),
    chargeName: formData.get(`reservation[charge_name]`),
    email: formData.get(`reservation[email]`),
    tel: formData.get(`reservation[tel]`),
    startDate: formData.get(`reservation[start_day]`),
    endDate: formData.get(`reservation[end_day]`),
    contents: formData.get(`reservation[contents]`),
    space: formData.get(`reservation[space]`),
    oneStopServices: formData.getAll(`reservation[one_stop_services]`),
    publish: formData.get(`reservation[publish]`),
    note: formData.get(`reservation[note]`),
  });
  if (!result.success) {
    return json({ ok: false, errors: result.error.flatten() }, { status: 400 });
  }

  const contentsText = sanitizeText(result.data.contents);
  const noteText = result.data.note ? sanitizeText(result.data.note) : undefined;
  const sendResult = await sendReservation({
    ...result.data,
    startDate: dayjs(result.data.startDate).locale("ja").format("YYYY年M月D日(ddd)"),
    endDate: dayjs(result.data.endDate).locale("ja").format("YYYY年M月D日(ddd)"),
    contents: contentsText,
    note: noteText,
  });

  if (!sendResult) {
    return json(
      {
        ok: false,
        errors: {
          formErrors: ["送信エラーが発生しました。ウェブサイト管理者へお問い合わせ下さい。"],
          fieldErrors: null,
        },
      },
      { status: 500 }
    );
  }

  session.flash("_reservation_sent", true);

  return redirect("/contact/reservation/success", { headers: await commitSession(session) });
}

export default function Reservation() {
  const { csrf } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const transition = useTransition();
  const formID = useID();

  const groupNameID = useID();
  const chargeNameID = useID();
  const emailID = useID();
  const telID = useID();
  const dateID = useID();
  const contentsID = useID();
  const spaceID = useID();
  const oneStopServicesID = useID();
  const publishID = useID();
  const noteID = useID();

  return (
    <PageContainer>
      <ArticleLayout
        size="small"
        paths={[
          { name: "ホーム", url: "/" },
          { name: "お問い合わせ", url: "/contact" },
          { name: "専用利用の仮申込", url: "/contact/reservation" },
        ]}
      >
        <div className={clsx("flex", "flex-col", "space-y-8")}>
          <DisplayText element="h1" size="extraLarge">
            専用利用の仮申込
          </DisplayText>

          <div className="relative">
            <section className={clsx("border-b", "border-b-gray-200", "pb-8", "mb-8")}>
              <p>
                万代島多目的広場の専用利用の仮申込を受け付けます。
                <br />
                <Link to="/vacancies" className="button button--plain" rel="noreferrer noopener" target="_blank">
                  会場の予約状況を確認する
                </Link>
              </p>
            </section>

            <Form id={formID} method="post" aria-describedby={errorTextID(formID)}>
              <input type="hidden" name="_csrf" value={csrf} />
              <div className={clsx("flex", "flex-col", "space-y-4")}>
                {actionData?.errors?.formErrors && actionData.errors.formErrors.length > 0 && (
                  <FieldsError id={errorTextID(formID)}>{actionData.errors.formErrors}</FieldsError>
                )}
                <div className={clsx("flex", "flex-col", "space-y-8")}>
                  <FieldsInput
                    id={groupNameID}
                    name={`reservation[group_name]`}
                    type="text"
                    autoComplete="organization"
                    maxLength={255}
                    size={255}
                    labelText="企業・団体名"
                    errorText={actionData?.errors.fieldErrors?.groupName}
                    disabled={transition.state === "submitting"}
                  />

                  <FieldsInput
                    id={chargeNameID}
                    name={`reservation[charge_name]`}
                    type="text"
                    autoComplete="off"
                    maxLength={255}
                    size={255}
                    labelText="代表者・担当者名"
                    errorText={actionData?.errors?.fieldErrors?.chargeName}
                    disabled={transition.state === "submitting"}
                  />

                  <FieldsInput
                    id={emailID}
                    name={`reservation[email]`}
                    type="email"
                    autoComplete="email"
                    labelText="Eメールアドレス"
                    errorText={actionData?.errors?.fieldErrors?.email}
                    disabled={transition.state === "submitting"}
                  />

                  <FieldsInput
                    id={telID}
                    name={`reservation[tel]`}
                    type="text"
                    autoComplete="tel"
                    maxLength={20}
                    size={20}
                    labelText="電話番号"
                    errorText={actionData?.errors?.fieldErrors?.tel}
                    disabled={transition.state === "submitting"}
                  />

                  <FieldsDateRangePicker
                    id={dateID}
                    name={(text) => `reservation[${text}]`}
                    labelText="専用利用希望日程"
                    errorText={
                      (actionData?.errors.fieldErrors?.startDate || actionData?.errors.fieldErrors?.endDate) &&
                      ([actionData.errors.fieldErrors.startDate, actionData.errors.fieldErrors.endDate].filter(
                        Boolean
                      ) as unknown as string[])
                    }
                    startLabelText="専用利用開始日"
                    startErrorText={actionData?.errors.fieldErrors?.startDate}
                    endLabelText="専用利用終了日"
                    endErrorText={actionData?.errors.fieldErrors?.endDate}
                    disabled={transition.state === "submitting"}
                  />

                  <FieldsTextarea
                    id={contentsID}
                    name={`reservation[contents]`}
                    rows={4}
                    maxLength={1000}
                    labelText="ご利用の内容"
                    errorText={actionData?.errors?.fieldErrors?.contents}
                    placeholder="できるだけ詳しくご入力ください。"
                    disabled={transition.state === "submitting"}
                  />

                  <FieldsRadioGroup
                    id={spaceID}
                    name={`reservation[space]`}
                    values={["屋内半面", "屋内全面", "屋外", "placeholder", "屋内外(両方、但し屋内は全面利用)"]}
                    defaultValue="屋内"
                    labelText="希望スペース"
                    errorText={actionData?.errors.fieldErrors?.space}
                    disabled={transition.state === "submitting"}
                  />

                  <FieldsCheckboxGroup
                    id={oneStopServicesID}
                    name={`reservation[one_stop_services]`}
                    // @ts-ignore
                    values={oneStopServices}
                    labelText="ワンストップサービス (有料・追加手配)"
                    errorText={actionData?.errors.fieldErrors?.oneStopServices}
                    disabled={transition.state === "submitting"}
                  />

                  <FieldsRadioGroup
                    id={publishID}
                    name={`reservation[publish]`}
                    values={["可", "不可"]}
                    defaultValue="可"
                    labelText="万代島多目的広場イベントスケジュール一覧への記載"
                    errorText={actionData?.errors.fieldErrors?.publish}
                    disabled={transition.state === "submitting"}
                  />

                  <FieldsTextarea
                    id={noteID}
                    name={`reservation[note]`}
                    rows={4}
                    maxLength={1000}
                    labelText="その他要望など"
                    errorText={actionData?.errors.fieldErrors?.note}
                    disabled={transition.state === "submitting"}
                  />

                  <div>
                    <p className={clsx("text-xs", "leading-7")}>
                      ※入力前に
                      <a
                        href="/vacancies"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={clsx("text-blue-500", "underline")}
                      >
                        会場の予約状況ページ
                      </a>
                      で希望日程の空き状況をご確認ください。
                      <br />
                      ※仮予約受付確認後、改めて連絡差し上げます。
                      <br />
                      ※この申し込みは仮予約となります。本予約は打合せ後、イベント内容を確認したうえで本予約申請書をご提出いただきます。
                    </p>
                  </div>

                  <div className={clsx("flex", "justify-end")}>
                    <button
                      type="submit"
                      className="button button--primary"
                      disabled={transition.state === "submitting"}
                    >
                      <span className="button__backdrop" />
                      <span className="button__content">送信</span>
                    </button>
                  </div>
                </div>
              </div>
            </Form>

            {transition.state === "submitting" && <Loading.Absolute />}
          </div>
        </div>
      </ArticleLayout>
    </PageContainer>
  );
}
